import {
  IonItem,
  IonLabel,
  IonButton,
  IonModal,
  IonContent,
  IonList,
  IonCheckbox,
  IonText,
} from "@ionic/react";
import { Controller } from "react-hook-form";
import type { Control, FieldValues, FieldPath } from "react-hook-form";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getOrganisations } from "@/api";
import React, { useState, useMemo } from "react";

import "./SelectOrganisation.css";
import { AppHeader } from "./AppHeader";
import { Did, Organisation } from "@/types";

// Props for the input component
interface SelectOrganisationInputProps {
  value?: Did;
  disabled?: boolean;
  onOrganisationSelect: (organisation: Organisation) => void;
  selectedOrganisation?: Organisation;
  className?: string;
  children?: (props: {
    onClick: () => void;
    disabled?: boolean;
  }) => React.ReactNode;
}

// Props for the control wrapper
interface SelectOrganisationControlProps<T extends FieldValues> {
  control: Control<T>;
  required: boolean;
  name: FieldPath<T>;
  disabled: boolean;
  className?: string;
}

// The input component without form control logic
const SelectOrganisationInput: React.FC<SelectOrganisationInputProps> = ({
  children,
  value,
  className,
  disabled,
  onOrganisationSelect,
  selectedOrganisation: selectedOrganisationProp,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();

  const getSelectedOrganisation = (
    selectedId: Did | null | undefined
  ): Organisation | undefined => {
    if (!selectedId) return undefined;

    // If selectedOrganisation is passed as prop, use it
    if (
      selectedOrganisationProp &&
      selectedOrganisationProp.id === selectedId
    ) {
      return selectedOrganisationProp;
    }

    // First try to find in current loaded pages
    const found = allOrganisations.find((p) => p.id === selectedId);
    if (found) return found;

    // If not found, check React Query cache for individual organisation
    const cached = queryClient.getQueryData(["organisation", selectedId]);
    return cached as Organisation | undefined;
  };

  const { data, isLoading } = useQuery({
    queryKey: ["organisations"],
    queryFn: () => getOrganisations(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const allOrganisations = useMemo(() => {
    return data?.organisations ?? [];
  }, [data]);

  const handleOrganisationSelect = (organisation: Organisation) => {
    // Cache the selected organisation data
    queryClient.setQueryData(["organisation", organisation.id], organisation);
    onOrganisationSelect(organisation);
    setIsOpen(false);
  };

  const selectedOrganisation = getSelectedOrganisation(value);

  const handleKeepCurrent = () => {
    if (selectedOrganisation) {
      onOrganisationSelect(selectedOrganisation);
      setIsOpen(false);
    }
  };

  return (
    <>
      {children ? (
        children({ onClick: () => setIsOpen(true), disabled })
      ) : (
        <IonItem className={className}>
          <IonLabel position="stacked">Wissel van organisatie</IonLabel>
          <IonButton
            fill="outline"
            expand="block"
            disabled={disabled}
            onClick={() => setIsOpen(true)}
          >
            {selectedOrganisation
              ? selectedOrganisation.name
              : "Selecteer organisatie"}
          </IonButton>
        </IonItem>
      )}
      <IonModal
        isOpen={isOpen}
        onDidDismiss={() => setIsOpen(false)}
        className="select-organisation-modal"
      >
        <AppHeader
          title="Selecteer Organisation"
          onClose={() => setIsOpen(false)}
        />

        <IonContent>
          <div
            style={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            {/* Fixed header area */}
            <div>
              {/* Currently selected organisation */}
              {selectedOrganisation && (
                <>
                  <IonItem color="light" lines="none">
                    <IonCheckbox slot="start" checked={true} />
                    <IonLabel>
                      <p className="ion-color-medium">Geselecteerd</p>
                      <h3 className="ion-text-wrap ion-text-bold">
                        {selectedOrganisation.name}
                      </h3>
                    </IonLabel>
                    <IonButton
                      slot="end"
                      fill="outline"
                      size="small"
                      onClick={handleKeepCurrent}
                    >
                      Keep
                    </IonButton>
                  </IonItem>
                  <div
                    className="ion-margin-horizontal"
                    style={{
                      height: "1px",
                      backgroundColor: "var(--ion-color-light-shade)",
                    }}
                  />
                </>
              )}
            </div>

            {/* Scrollable organisation list */}
            <div style={{ flex: 1, overflow: "hidden" }}>
              <IonContent scrollEvents={true}>
                <IonList className="bg-none">
                  {allOrganisations.map((organisation) => (
                    <IonItem
                      key={organisation.id}
                      button
                      onClick={() => handleOrganisationSelect(organisation)}
                    >
                      <IonCheckbox
                        className="ion-margin-end"
                        slot="start"
                        checked={value === organisation.id}
                      />
                      <IonLabel>
                        <h3>{organisation.name}</h3>
                      </IonLabel>
                    </IonItem>
                  ))}

                  {allOrganisations.length === 0 && !isLoading && (
                    <IonItem>
                      <IonLabel>
                        <IonText color="medium">
                          Je bent niet bij een organisatie aangesloten
                        </IonText>
                      </IonLabel>
                    </IonItem>
                  )}
                </IonList>
              </IonContent>
            </div>
          </div>
        </IonContent>
      </IonModal>
    </>
  );
};

// The control wrapper component
const SelectOrganisationControl = <T extends FieldValues>({
  control,
  className,
  required,
  name,
  disabled,
}: SelectOrganisationControlProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={required ? { required: "Selecteer een organisatie" } : {}}
      render={({ field }) => (
        <SelectOrganisationInput
          className={className}
          value={field.value}
          disabled={disabled}
          onOrganisationSelect={field.onChange}
        />
      )}
    />
  );
};

export { SelectOrganisationInput, SelectOrganisationControl };
